import { i18n } from "@lingui/core";
import {
  buildFinaVerificationUrl,
  buildUpnQrCodeString,
  encodeIso88592,
  formatCurrency,
} from "../../../../shared/utils/utils";
import {
  GiftCardDocument,
  GiftCardReceiptDocument,
  GiftCardTransactionAccountDocument,
  InvoiceDocument,
  PrinterConfig,
  TransactionAccountReceiptDocument,
  PaymentType,
  ClosureDocument,
} from "./types";
import {
  buildEscPosQrCode,
  getCompanyNumberLabel,
  getPaddingCommands,
  getPaymentTypeLabel,
  getTaxIdentificationLabel,
  getUpnQrModuleSize,
} from "./utils";
import dayjs from "dayjs";
import { t } from "@lingui/core/macro";
import { replaceSumniki } from "@/pages/Dashboard/Customers/BulkSMSModal";

export const renderInvoiceToEscPos = ({
  invoiceData: invoice,
  printerConfig,
  additionalSettings,
  isCopy,
}: {
  invoiceData: InvoiceDocument["invoiceData"];
  printerConfig: PrinterConfig;
  additionalSettings?: {
    receiptHeader?: string;
    receiptFooter?: string;
  };
  isCopy: boolean;
}) => {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const encoder = new TextEncoder();

  const lineWidth = printerConfig.lineWidth;

  const itemNameMaxLength = lineWidth; // Entire line width for item name
  const itemPriceMaxLength = 12; // Length for price (Cena)
  const itemQuantityMaxLength = 4; // Length for quantity (Kol)
  const itemDiscountMaxLength = 7; // Length for discount (Popust)
  const itemTotalMaxLength = 9; // Length for total value (Vrednost)

  const taxRateMaxLength = 7; // Length for tax rate
  const taxNetPriceMaxLength = 9; // Length for tax net price
  const taxTotalMaxLength = 7; // Length for tax gross price
  const taxGrossPriceMaxLength = 9; // Length for tax gross price

  // Format a single item into two lines
  const formatItemLines = (
    name: string,
    price: string,
    quantity: number,
    discount: string,
    total: string,
  ) => {
    const namePadded = name
      .substring(0, itemNameMaxLength - 1)
      .padEnd(itemNameMaxLength - 1); // Ensure the name is left-aligned
    const pricePadded = price.padStart(itemPriceMaxLength);
    const quantityPadded = `${quantity}x`.padStart(itemQuantityMaxLength);
    const discountPadded = (discount || "").padStart(itemDiscountMaxLength);
    const totalPadded = total.padStart(itemTotalMaxLength);
    const secondLine = `${pricePadded}${quantityPadded}${discountPadded}${totalPadded}\n`;
    return `${namePadded}\n${secondLine}`;
  };

  const itemsArray = invoice.additionalReceiptData.invoiceData.items.map(
    (item) =>
      encoder.encode(
        formatItemLines(
          item.name, // Item name
          item.price.toFixed(2), // Cena
          item.quantity, // Kol
          item.discount ?? "", // Popust (empty string if not provided)
          item.totalWithTax.toFixed(2), // Vrednost
        ),
      ),
  );

  const flattenedItemsArray = itemsArray.reduce((acc, val) => {
    const newArray = new Uint8Array(acc.length + val.length);
    newArray.set(acc);
    newArray.set(val, acc.length);
    return newArray;
  }, new Uint8Array());

  // Format the tax details into a properly aligned string
  const formatTaxLine = (
    taxRate: string,
    netPrice: string,
    taxTotal: string,
    grossPrice: string,
  ): string => {
    const taxRatePadded = taxRate.padStart(taxRateMaxLength);
    const netPricePadded = netPrice.padStart(taxNetPriceMaxLength);
    const taxTotalPadded = taxTotal.padStart(taxTotalMaxLength);
    const grossPricePadded = grossPrice.padStart(taxGrossPriceMaxLength);
    return `${taxRatePadded}${netPricePadded}${taxTotalPadded}${grossPricePadded}\n`;
  };

  let totalNetPrice = 0;
  let totalTaxAmount = 0;
  let totalGrossPrice = 0;

  const invoiceTaxesArray =
    invoice.additionalReceiptData.invoiceData.invoiceTaxes.map((tax) => {
      const netPrice = tax.base;
      const taxAmount = tax.totalTaxAmount;
      const grossPrice = netPrice + taxAmount;

      // Accumulate totals
      totalNetPrice += netPrice;
      totalTaxAmount += taxAmount;
      totalGrossPrice += grossPrice;

      return encoder.encode(
        formatTaxLine(
          tax.taxRate.toFixed(1), // DDV %
          netPrice.toFixed(2), // Neto
          taxAmount.toFixed(2), // DDV
          grossPrice.toFixed(2), // Bruto
        ),
      );
    });

  const totalsLine = encoder.encode(
    formatTaxLine(
      `${t`SKUPAJ`}:`, // No tax rate for totals
      totalNetPrice.toFixed(2), // Total Neto
      totalTaxAmount.toFixed(2), // Total DDV
      totalGrossPrice.toFixed(2), // Total Bruto
    ),
  );

  const flattenedInvoiceTaxesArray = invoiceTaxesArray.reduce((acc, val) => {
    const newArray = new Uint8Array(acc.length + val.length);
    newArray.set(acc);
    newArray.set(val, acc.length);
    return newArray;
  }, new Uint8Array());

  // Encode a divider line
  const divider = encoder.encode("-".repeat(lineWidth - 1) + "\n");

  // Concatenate the divider and totals line to the flattened array
  const taxesWithDividerAndTotals = new Uint8Array(
    flattenedInvoiceTaxesArray.length + divider.length + totalsLine.length,
  );
  taxesWithDividerAndTotals.set(flattenedInvoiceTaxesArray);
  taxesWithDividerAndTotals.set(divider, flattenedInvoiceTaxesArray.length);
  taxesWithDividerAndTotals.set(
    totalsLine,
    flattenedInvoiceTaxesArray.length + divider.length,
  );

  const qrCodeString = (() => {
    // FURS returns a ready-made QR payload; FINA does not, so the verification
    // URL is assembled from the invoice's fiscalization identifiers.
    if (invoice.provider === "furs") {
      return invoice.additionalReceiptData.QR ?? null;
    }

    if (invoice.provider === "fina") {
      return buildFinaVerificationUrl({
        jir: invoice.additionalReceiptData.JIR,
        zki: invoice.additionalReceiptData.ZKI,
        finaVerifyDate: invoice.finaVerifyDate,
        totalWithTax: invoice.totalWithTax,
      });
    }

    return null;
  })();

  const rawQrCodeData = qrCodeString
    ? buildEscPosQrCode(encoder.encode(qrCodeString))
    : [];
  const companyData = invoice.additionalReceiptData._documentIssuer;
  const bpData = invoice.additionalReceiptData.businessPremiseData;
  const customerData = invoice.additionalReceiptData.customerData;

  const companyPrintCommands =
    companyData != null
      ? [
          ...encoder.encode(`${companyData.name}\n`),
          ...(companyData.address
            ? encoder.encode(`${companyData.address}\n`)
            : []),
          ...(companyData.zip
            ? encoder.encode(`${companyData.zip} ${companyData.city}\n`)
            : []),
          ...encoder.encode(
            `${getTaxIdentificationLabel(invoice.provider, companyData.isTaxSubject)}: ${companyData.taxNumber || "/"}\n`,
          ),
          ...(companyData.companyNumber
            ? encoder.encode(
                `${getCompanyNumberLabel(invoice.provider)}: ${companyData.companyNumber}\n`,
              )
            : []),
        ]
      : [];

  const bpPrintCommands =
    bpData != null
      ? [
          ...encoder.encode(`\n\n${bpData.businessPremiseId}\n`),
          ...encoder.encode(
            `${bpData.street} ${bpData.houseNumber ?? ""}${bpData.houseNumberAdditional ?? ""}\n`,
          ),
          ...encoder.encode(
            `${bpData.postalCode ?? ""} ${bpData.city ?? ""}\n`,
          ),
        ]
      : [];

  const customerPrintCommands =
    customerData != null
      ? [
          ...encoder.encode(`\n\n${customerData.name}\n`),
          ...(customerData.address
            ? encoder.encode(`${customerData.address}\n`)
            : []),
          ...(customerData.zip && customerData.city
            ? encoder.encode(`${customerData.zip} ${customerData.city}\n`)
            : []),
          ...encoder.encode(`ID za DDV: ${customerData.taxNumber || "/"}\n`),
        ]
      : [];

  const paymentMethodAmountsCommands =
    invoice.additionalReceiptData.payments.map((payment) => {
      const typePadded = getPaymentTypeLabel(
        payment.type as PaymentType,
      ).padEnd(lineWidth - 10); // Adjust padding based on your needs
      const amountPadded = payment.amount.toFixed(2).padStart(9); // Right align the amount
      return encoder.encode(`${typePadded}${amountPadded}\n`);
    });

  const flattenedPaymentMethodAmountsArray =
    paymentMethodAmountsCommands.reduce((acc, val) => {
      const newArray = new Uint8Array(acc.length + val.length);
      newArray.set(acc);
      newArray.set(val, acc.length);
      return newArray;
    }, new Uint8Array());

  const printCount = invoice.additionalReceiptData.printCount + 1;

  const includesMedicineService =
    invoice.additionalReceiptData.invoiceData.items.some(
      (item) => item.custom.isMedicineService,
    );

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "escpos_generic",
    paddingTop: printerConfig.paddingTop,
    paddingBottom: printerConfig.paddingBottom,
    encoder,
  });

  const receiptHeaderCommands = additionalSettings?.receiptHeader
    ? [
        ...encoder.encode(
          `${replaceSumniki(additionalSettings.receiptHeader)}\n`,
        ),
        0x0a, // New line
        0x0a, // New line
        0x0a, // New line
      ]
    : [];
  const receiptFooterCommands = additionalSettings?.receiptFooter
    ? [
        0x0a, // New line
        0x0a, // New line
        0x0a, // New line
        ...encoder.encode(
          `${replaceSumniki(additionalSettings.receiptFooter)}\n`,
        ),
      ]
    : [];

  const commands = [
    0x0a, // New line
    0x0a, // New line

    ...paddingTopCommands,

    ...receiptHeaderCommands,

    // Print company info in normal size
    0x1b,
    0x21,
    0x00, // Normal size (ESC ! 0)
    ...companyPrintCommands,

    ...bpPrintCommands,

    // Print customer info if exists
    ...customerPrintCommands,

    0x0a, // New line
    0x0a, // New line

    // Print copy if isCopy
    ...(isCopy && printCount > 1
      ? encoder.encode(`${t`Kopija`} ${printCount - 1}\n\n\n`)
      : []),

    // Print RACUN ST. in bold and normal text size
    0x1b,
    0x45,
    0x01, // Turn on bold (ESC E 1)
    ...(invoice.canceled ? encoder.encode(`${t`STORNIRAN RACUN`}\n`) : []),
    ...encoder.encode(`${t`RACUN ST.`}: ${invoice.number}\n`),
    0x1b,
    0x45,
    0x00, // Turn off bold (ESC E 0)

    // turn off center alignment
    0x1b,
    0x61,
    0x00,

    0x0a, // New line

    // Print table header with fixed spacing
    ...encoder.encode(
      `${t`Artikel/Cena`} ${t`Kol`} ${t`Popust`} ${t`Vrednost`}\n`,
    ),
    0x1b,
    0x45,
    0x01, // Turn on bold (ESC E 1)
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),
    0x1b,
    0x45,
    0x00, // Turn off bold (ESC E 0)

    ...flattenedItemsArray,

    0x1b,
    0x45,
    0x01, // Turn on bold (ESC E 1)
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),
    0x1b,
    0x45,
    0x00, // Turn off bold (ESC E 0)

    // Print total
    ...encoder.encode(
      `${t`SKUPAJ`}: ${invoice.totalWithoutDiscount.padStart(lineWidth - 9)}\n`,
    ),
    ...(invoice.totalDiscount != 0
      ? encoder.encode(
          `${t`POPUST`}: ${`-${invoice.totalDiscountFormatted}`.padStart(lineWidth - 9)}\n`,
        )
      : []),
    // Print "ZA PLACILO" in normal size
    // Reset alignment to left
    0x1b,
    0x61,
    0x00, // Left alignment

    // Print "ZA PLACILO" in normal size
    ...encoder.encode(`${t`ZA PLACILO`} ${invoice.currencyId.padEnd(5)}`),

    // Switch to larger text size for the total amount on the same line
    // 0x1b,
    // 0x21,
    // 0x30, // Double height and double width (ESC ! 0x30)
    ...encoder.encode(invoice.total.padStart(lineWidth - 17)),

    // Reset to normal text size
    0x1b,
    0x21,
    0x00, // Reset to normal text size

    0x0a, // New line
    0x0a, // New line

    ...flattenedPaymentMethodAmountsArray,

    0x0a, // New line

    // Print tax details
    ...encoder.encode(
      `--${invoice.provider === "furs" ? `DDV %` : "PDV %"}-----${t`Neto`}----${invoice.provider === "furs" ? `DDV` : "PDV"}----${t`Bruto`}${"-".repeat(Math.max(0, lineWidth - 33))}\n`,
    ),
    ...taxesWithDividerAndTotals,
    0x0a, // New line

    // Print seller info in a smaller size
    0x1b,
    0x21,
    0x01, // Slightly smaller text size
    // Print date and time
    ...encoder.encode(
      `${invoice.location.city ? invoice.location.city + ", " : ""}${invoice.date}\n`,
    ),
    ...(invoice.additionalReceiptData.employeeData?.name
      ? encoder.encode(
          `${t`Racun izdal`}: ${invoice.additionalReceiptData.employeeData.name}\n`,
        )
      : []),
    0x1b,
    0x21,
    0x00, // Reset to normal text size

    0x0a, // New line

    // Print ZOI and EOR
    ...(invoice.additionalReceiptData.ZOI != null
      ? encoder.encode(`${t`ZOI`}: ${invoice.additionalReceiptData.ZOI}\n`)
      : []),
    ...(invoice.additionalReceiptData.EOR != null
      ? encoder.encode(`${t`EOR`}: ${invoice.additionalReceiptData.EOR}\n`)
      : []),
    // Print ZKI and JIR
    ...(invoice.additionalReceiptData.ZKI != null
      ? encoder.encode(`${t`ZKI`}: ${invoice.additionalReceiptData.ZKI}\n`)
      : []),
    ...(invoice.additionalReceiptData.JIR != null
      ? encoder.encode(`${t`JIR`}: ${invoice.additionalReceiptData.JIR}\n`)
      : []),
    0x0a, // New line

    ...rawQrCodeData,

    ...(!companyData.isTaxSubject && invoice.provider === "furs"
      ? [
          0x01, // Slightly smaller text size
          ...encoder.encode(
            "DDV ni obracunan na podlagi 1. odstavka 94. clena ZDDV-1.",
          ),
          0x00, // Reset to normal text size
          0x0a,
          0x0a,
        ]
      : []),
    ...(!companyData.isTaxSubject && invoice.provider === "fina"
      ? [
          0x01, // Slightly smaller text size
          ...encoder.encode(
            "PDV nije obračunat temeljem članka 90. stavak 1. i 2. Zakona o PDV-u.",
          ),
          0x00, // Reset to normal text size
          0x0a,
          0x0a,
        ]
      : []),

    ...(includesMedicineService &&
    invoice.provider === "furs" &&
    companyData.isTaxSubject
      ? [
          0x01, // Slightly smaller text size
          ...encoder.encode(
            "DDV ni obracunan v skladu z 2. tocko 1. odstavka 42. clena ZDDV-1.",
          ),
          0x00, // Reset to normal text size
          0x0a,
          0x0a,
        ]
      : []),

    ...(includesMedicineService &&
    invoice.provider === "fina" &&
    companyData.isTaxSubject
      ? [
          0x01, // Slightly smaller text size
          ...encoder.encode(
            "PDV nije obračunat sukladno članku 39. stavak 1. točka b) Zakona o PDV-u.",
          ),
          0x00, // Reset to normal text size
          0x0a,
          0x0a,
        ]
      : []),

    // Print centered www.lime-booking.si website url
    // Center alignment for the website URL
    0x1b,
    0x61,
    0x01, // Center alignment (ESC a 1)
    ...encoder.encode(`${t`Hvala za obisk!`}`),
    0x0a,
    ...encoder.encode(`${t`www.lime-booking.si`}`),
    0x0a,

    ...receiptFooterCommands,

    ...paddingBottomCommands,

    // Add some space at the bottom and cut paper if supported
    0x0a,
    0x0a,
  ];

  return commands;
};

export const renderGiftCardToEscPos = ({
  doc,
  printerConfig,
}: {
  doc: GiftCardDocument;
  printerConfig: PrinterConfig;
}) => {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const encoder = new TextEncoder();

  const { organization, giftCard, operatorLabel, currencyId } = doc.data;

  const { locale } = i18n;

  const initialAmountFormatted = formatCurrency({
    amount: giftCard.initialAmountCents / 100,
    currency: currencyId,
    locale,
    options: {
      currencyDisplay: "code",
    },
    shouldSanitize: true,
  });

  const amountLeftFormatted = formatCurrency({
    amount: giftCard.amountLeftCents / 100,
    currency: currencyId,
    locale,
    options: {
      currencyDisplay: "code",
    },
    shouldSanitize: true,
  });

  const companyPrintCommands =
    organization != null
      ? [
          ...encoder.encode(`${organization.name}\n`),
          ...(organization.address
            ? encoder.encode(`${organization.address}\n`)
            : []),
          ...(organization.zip
            ? encoder.encode(`${organization.zip} ${organization.city}\n`)
            : []),
          ...encoder.encode(
            `${getTaxIdentificationLabel("furs", organization.isTaxSubject)}: ${organization.taxNumber || "/"}\n`,
          ),
          ...(organization.companyNumber
            ? encoder.encode(
                `${getCompanyNumberLabel("furs")}: ${organization.companyNumber}\n`,
              )
            : []),
        ]
      : [];

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "escpos_generic",
    paddingTop: printerConfig.paddingTop,
    paddingBottom: printerConfig.paddingBottom,
    encoder,
  });

  return [
    0x0a,
    0x0a,

    ...paddingTopCommands,

    0x1b,
    0x61,
    0x01,
    0x00, // Normal size

    ...companyPrintCommands,

    0x0a,

    ...encoder.encode(`${t`Blagajnik`}: ${operatorLabel}\n`),

    0x0a,

    ...encoder.encode(`${t`DARILNI BON`}\n`),
    ...encoder.encode(`${initialAmountFormatted}\n`),

    ...(giftCard.initialAmountCents !== giftCard.amountLeftCents
      ? [
          ...encoder.encode(`${t`Preostali znesek`}\n`),
          ...encoder.encode(`${amountLeftFormatted}\n`),
        ]
      : []),

    0x0a,

    ...encoder.encode(`${t`KODA`}:\n`),
    ...encoder.encode(`${giftCard.code}\n`),

    0x0a,

    ...encoder.encode(`${t`Veljavnost od`}: ${giftCard.issueDate}\n`),
    ...encoder.encode(`${t`Veljavnost do`}: ${giftCard.expiryDate}\n`),

    0x0a,

    ...encoder.encode(`${t`Hvala za obisk!`}\n`),

    0x0a,

    ...encoder.encode(`${t`www.lime-booking.si`}\n`),

    0x0a, // New line
    0x0a, // New line
    0x0a, // New line
    0x0a, // New line
    0x0a, // New line

    ...paddingBottomCommands,
  ];
};

export const renderGiftCardReceiptToEscPos = ({
  doc,
  printerConfig,
}: {
  doc: GiftCardReceiptDocument;
  printerConfig: PrinterConfig;
}) => {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const encoder = new TextEncoder();

  const { locale } = i18n;

  const {
    organization,
    operatorLabel,
    payments,
    issuedGiftCards,
    currencyId,
    dateTime,
    location,
  } = doc.data;

  const lineWidth = printerConfig.lineWidth;

  const receivedPaymentsArray = Object.entries(payments).flatMap(
    ([type, data]) => {
      const paymentTypeLabel = getPaymentTypeLabel(type as PaymentType);

      const amountFormatted = formatCurrency({
        amount: data.amountCents / 100,
        currency: currencyId,
        locale,
        options: {
          currencyDisplay: "code",
        },
        shouldSanitize: true,
      });
      return [
        encoder.encode(
          `${paymentTypeLabel}: ${amountFormatted.padStart(lineWidth - paymentTypeLabel.length - 3)}\n`,
        ),
      ];
    },
  );
  const flattenedReceivedPaymentsArray = receivedPaymentsArray.reduce(
    (acc, val) => {
      const newArray = new Uint8Array(acc.length + val.length);
      newArray.set(acc);
      newArray.set(val, acc.length);
      return newArray;
    },
    new Uint8Array(),
  );

  const issuedGiftCardsArray = issuedGiftCards.map((igc) => {
    const initialAmountFormatted = formatCurrency({
      amount: igc.initialAmountCents / 100,
      currency: currencyId,
      locale,
      options: {
        currencyDisplay: "code",
      },
      shouldSanitize: true,
    });

    return encoder.encode(
      `${t`DARILNI BON`}${initialAmountFormatted.padStart(lineWidth - 12)}\n`,
    );
  });
  const flattenedIssuedGiftCardsArray = issuedGiftCardsArray.reduce(
    (acc, val) => {
      const newArray = new Uint8Array(acc.length + val.length);
      newArray.set(acc);
      newArray.set(val, acc.length);
      return newArray;
    },
    new Uint8Array(),
  );

  const companyPrintCommands =
    organization != null
      ? [
          ...encoder.encode(`${organization.name}\n`),
          ...(organization.address
            ? encoder.encode(`${organization.address}\n`)
            : []),
          ...(organization.zip
            ? encoder.encode(`${organization.zip} ${organization.city}\n`)
            : []),
          ...encoder.encode(
            `${getTaxIdentificationLabel("furs", organization.isTaxSubject)}: ${organization.taxNumber || "/"}\n`,
          ),
          ...(organization.companyNumber
            ? encoder.encode(
                `${getCompanyNumberLabel("furs")}: ${organization.companyNumber}\n`,
              )
            : []),
        ]
      : [];

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "escpos_generic",
    paddingTop: printerConfig.paddingTop,
    paddingBottom: printerConfig.paddingBottom,
    encoder,
  });

  return [
    // A couple of blank lines at the start
    0x0a,
    0x0a,

    ...paddingTopCommands,

    // Center alignment: ESC a 1
    0x1b,
    0x61,
    0x01,
    0x00, // Normal text size (optional)

    // Organization info
    ...companyPrintCommands,

    0x0a, // blank line

    // Left alignment for "Blagajnik"
    0x1b,
    0x61,
    0x00,
    ...encoder.encode(`${t`Blagajnik`}: ${operatorLabel}\n`),

    0x0a,

    // "Prejeta plačilna sredstva" (payments) in normal size
    ...encoder.encode(`${t`Prejeta placilna sredstva`}\n`),
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),

    // Print each payment line
    ...flattenedReceivedPaymentsArray,

    0x0a, // blank line

    // "Izdana plačilna sredstva" (issued gift cards)
    ...encoder.encode(`${t`Izdana placilna sredstva`}\n`),
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),

    // Print each gift card
    ...flattenedIssuedGiftCardsArray,

    0x0a, // blank line
    0x0a, // blank line

    ...encoder.encode(`${location}, ${dateTime}\n`),

    0x0a, // blank line
    0x0a, // blank line

    // Center alignment again for "Hvala za obisk!"
    0x1b,
    0x61,
    0x01,
    ...encoder.encode(`${t`Hvala za obisk!`}\n`),
    ...encoder.encode(`${t`www.lime-booking.si`}\n`),

    // Some extra new lines at the bottom
    0x0a,
    0x0a,
    0x0a,
    0x0a,

    ...paddingBottomCommands,
  ];
};

export const renderTransactionAccountReceiptToEscPos = ({
  doc,
  printerConfig,
}: {
  doc: TransactionAccountReceiptDocument;
  printerConfig: PrinterConfig;
}) => {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const encoder = new TextEncoder();

  const { data } = doc;
  const purpose = data.invoiceNumber;

  const { locale } = i18n;

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "escpos_generic",
    paddingTop: printerConfig.paddingTop,
    paddingBottom: printerConfig.paddingBottom,
    encoder,
  });

  return [
    0x0a,
    0x0a,

    ...paddingTopCommands,

    0x1b,
    0x61,
    0x01,
    0x00, // Normal size

    ...encoder.encode(`${t`Racun`}: ${data.invoiceNumber}\n`),
    ...encoder.encode(`${t`Znesek`}: ${data.amountFormatted}\n`),
    ...encoder.encode(`${t`Ime`}: ${data.name}\n`),
    ...encoder.encode(
      `${t`Naslov`}: ${data.address} ${data.city} ${data.country}\n`,
    ),
    ...encoder.encode(`${`IBAN`}: ${data.IBAN}\n`),
    ...encoder.encode(`${`SWIFT`}: ${data.SWIFT}\n`),
    ...encoder.encode(`${t`Referenca`}: ${data.reference}\n`),
    ...encoder.encode(`${t`Namen`}: ${purpose}\n`),
    ...encoder.encode(
      `${t`Rok placila`}: ${dayjs(data.dateDue).toDate().toLocaleDateString(locale)}\n`,
    ),

    ...buildEscPosQrCode(
      encodeIso88592(
        buildUpnQrCodeString({
          invoiceNumber: data.invoiceNumber,
          amount: data.amount,
          dueDate: dayjs(data.dateDue).format("DD.MM.YYYY"),
          paymentPurpose: purpose,
          purposeCode: "OTHR",
          recipientCity: data.city,
          recipientIban: data.IBAN,
          recipientName: data.name,
          recipientReference: data.reference,
          recipientStreet: data.address,
          payerCity: "",
          payerName: "",
          payerStreet: "",
        }),
      ),
      getUpnQrModuleSize(printerConfig.lineWidth),
    ),

    0x0a,
    0x0a,

    ...paddingBottomCommands,
  ];
};

export const renderGiftCardTransactionAccountToEscPos = ({
  doc,
  printerConfig,
}: {
  doc: GiftCardTransactionAccountDocument;
  printerConfig: PrinterConfig;
}) => {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const encoder = new TextEncoder();

  const { data } = doc;
  const purpose = data.giftCardInvoiceId;

  if (!data.organization.IBAN) {
    throw new Error("IBAN is required");
  }

  const { locale } = i18n;

  return [
    0x0a,
    0x0a,
    0x0a,
    0x0a,
    0x0a,

    0x1b,
    0x61,
    0x01,
    0x00, // Normal size

    ...encoder.encode(`${t`Racun`}: ${data.giftCardInvoiceId}\n`),
    ...encoder.encode(
      `${t`Znesek`}: ${data.amount.toFixed(2)} ${data.currency}\n`,
    ),
    ...encoder.encode(`${t`Ime`}: ${data.organization.name}\n`),
    ...encoder.encode(
      `${t`Naslov`}: ${data.organization.address} ${data.organization.city} ${data.organization.zip}\n`,
    ),
    ...encoder.encode(`${`IBAN`}: ${data.organization.IBAN ?? "/"}\n`),
    ...encoder.encode(`${`SWIFT`}: ${data.organization.SWIFT ?? "/"}\n`),
    ...encoder.encode(`${t`Referenca`}: ${data.reference ?? "/"}\n`),
    ...encoder.encode(`${t`Namen`}: ${purpose ?? "/"}\n`),
    ...encoder.encode(
      `${t`Rok placila`}: ${dayjs(data.dateDue).toDate().toLocaleDateString(locale)}\n`,
    ),

    ...buildEscPosQrCode(
      encodeIso88592(
        buildUpnQrCodeString({
          invoiceNumber: data.reference,
          amount: data.amount,
          dueDate: dayjs(data.dateDue).format("DD.MM.YYYY"),
          paymentPurpose: purpose,
          purposeCode: "OTHR",
          recipientCity: data.organization.city,
          recipientIban: data.organization.IBAN,
          recipientName: data.organization.name,
          recipientReference: data.reference,
          recipientStreet: data.organization.address,
          payerCity: "",
          payerName: "",
          payerStreet: "",
        }),
      ),
      getUpnQrModuleSize(printerConfig.lineWidth),
    ),

    0x0a,
    0x0a,
    0x0a,
    0x0a,
    0x0a,
  ];
};

export const renderClosureToEscPos = ({
  doc,
  printerConfig,
}: {
  doc: ClosureDocument;
  printerConfig: PrinterConfig;
}) => {
  if (!printerConfig) {
    throw new Error("Printer config is required");
  }

  const encoder = new TextEncoder();
  const { data } = doc;

  // Get line width (assuming 48 chars for most receipt printers)
  const lineWidth = printerConfig.lineWidth;

  // Helper function to create justified line (left text, right text)
  const createJustifiedLine = (leftText: string, rightText: string): string => {
    const totalLength = leftText.length + rightText.length;
    const padding = Math.max(1, lineWidth - totalLength);
    return leftText + " ".repeat(padding - 1) + rightText;
  };

  // Helper function to center text
  const centerText = (text: string): string => {
    const padding = Math.max(0, Math.floor((lineWidth - text.length) / 2));
    return " ".repeat(padding) + text;
  };

  // Calculate totals
  const totalAmount = data.documents
    .reduce((sum, doc) => sum + doc.totalWithTax, 0)
    .toFixed(2);

  const finalCashAmount = (data.amountByPaymentType.cash?.amount || 0).toFixed(
    2,
  );

  const { paddingTopCommands, paddingBottomCommands } = getPaddingCommands({
    printerType: "escpos_generic",
    paddingTop: printerConfig.paddingTop,
    paddingBottom: printerConfig.paddingBottom,
    encoder,
  });

  return [
    0x0a, // New line
    0x0a, // New line

    ...paddingTopCommands,

    // Center alignment for header
    0x1b,
    0x61,
    0x01, // Center alignment

    // Header section
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode(`${t`ZAKLJUCNA BLAGAJNA`}\n`),
    0x1b,
    0x45,
    0x00, // Bold off

    ...encoder.encode(`${t`Datum otvoritve`}: ${data.openDate}\n`),
    ...encoder.encode(`${t`Datum zakljucka`}: ${data.closeDate}\n`),

    0x0a, // New line

    // Left alignment for content
    0x1b,
    0x61,
    0x00, // Left alignment

    // Payment summary section
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode(`${t`Prejeta placilna sredstva`}\n`),
    0x1b,
    0x45,
    0x00, // Bold off
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),

    // Payment types
    ...Object.entries(data.amountByPaymentType)
      .filter(([_, amount]) => amount.amount > 0)
      .reduce((acc, [type, amount]) => {
        const label = replaceSumniki(getPaymentTypeLabel(type as PaymentType));
        const line = createJustifiedLine(`${label}:`, amount.amount.toFixed(2));
        return [...acc, ...encoder.encode(line + "\n")];
      }, [] as number[]),

    0x0a, // New line

    // VAT summary section
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode(`${t`Povzetek DDV`}\n`),
    0x1b,
    0x45,
    0x00, // Bold off
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),

    // VAT rates
    ...Object.entries(data.amountByTaxRate)
      .filter(([_, amount]) => amount.taxAmount > 0)
      .reduce((acc, [rate, amount]) => {
        const line = createJustifiedLine(
          `${t`DDV`} ${rate}%:`,
          amount.taxAmountFormatted,
        );
        return [...acc, ...encoder.encode(line + "\n")];
      }, [] as number[]),

    // VAT total
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),
    ...(() => {
      const totalVat =
        Object.values(data.amountByTaxRate).reduce(
          (sum, amount) => sum + (amount.taxAmount ?? 0),
          0,
        ) ?? 0;
      const line = createJustifiedLine(
        `${t`DDV skupaj`}:`,
        totalVat.toFixed(2).toString(),
      );
      return encoder.encode(line + "\n");
    })(),
    0x1b,
    0x45,
    0x00, // Bold off

    0x0a, // New line

    // Cash balance section
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode(`${t`Stanje blagajne`}\n`),
    0x1b,
    0x45,
    0x00, // Bold off
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),

    ...(() => {
      const line = createJustifiedLine(
        `${t`Koncni saldo blagajne`}:`,
        finalCashAmount,
      );
      return encoder.encode(line + "\n");
    })(),

    0x0a, // New line

    ...(() => {
      if (data.issuedGiftCardsByUser.length === 0) {
        return [];
      }

      return [
        0x1b,
        0x45,
        0x01, // Bold on
        ...encoder.encode(`${replaceSumniki(t`Izdani darilni boni`)}\n`),
        0x1b,
        0x45,
        0x00, // Bold off
        ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),
        ...data.issuedGiftCardsByUser.reduce((acc, user) => {
          acc.push(
            ...encoder.encode(
              `${replaceSumniki(`${user.name} ${user.lastName}`)}\n`,
            ),
          );

          user.giftCards.forEach((giftCard) => {
            acc.push(
              ...encoder.encode(
                createJustifiedLine(
                  replaceSumniki(giftCard.code),
                  giftCard.initialAmountFormatted,
                ) + "\n",
              ),
            );

            if (giftCard.cashAmountFormatted) {
              acc.push(
                ...encoder.encode(
                  createJustifiedLine(
                    `  ${replaceSumniki(getPaymentTypeLabel("cash"))}:`,
                    giftCard.cashAmountFormatted,
                  ) + "\n",
                ),
              );
            }

            if (giftCard.cardAmountFormatted) {
              acc.push(
                ...encoder.encode(
                  createJustifiedLine(
                    `  ${replaceSumniki(getPaymentTypeLabel("card"))}:`,
                    giftCard.cardAmountFormatted,
                  ) + "\n",
                ),
              );
            }

            if (giftCard.onlineAmountFormatted) {
              acc.push(
                ...encoder.encode(
                  createJustifiedLine(
                    `  ${replaceSumniki(getPaymentTypeLabel("online"))}:`,
                    giftCard.onlineAmountFormatted,
                  ) + "\n",
                ),
              );
            }
          });

          acc.push(
            ...encoder.encode(
              createJustifiedLine(
                `  ${replaceSumniki(t`Skupaj izdani darilni boni`)}:`,
                user.totalAmountFormatted,
              ) + "\n",
            ),
          );
          acc.push(...encoder.encode("-".repeat(lineWidth - 1) + "\n"));

          return acc;
        }, [] as number[]),
        0x0a, // New line
      ];
    })(),

    // Documents list header
    0x1b,
    0x61,
    0x01, // Center alignment
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode(`${t`SEZNAM RACUNOV`}\n`),
    0x1b,
    0x45,
    0x00, // Bold off
    0x1b,
    0x61,
    0x00, // Left alignment

    ...encoder.encode("=".repeat(lineWidth - 1) + "\n"),

    // Column headers
    ...encoder.encode(`${t`St.racuna`}    ${t`Datum`}       ${t`Skupaj`}\n`),
    ...encoder.encode("-".repeat(lineWidth - 1) + "\n"),

    // Documents list
    ...data.documents.reduce((acc, doc) => {
      const docDate = dayjs(doc.dateTime).format("DD.MM.YY HH:mm");
      const docNumber =
        doc.number.length > 12 ? doc.number.substring(0, 12) : doc.number;

      // Main document line
      const docLine = `${docNumber.padEnd(12)} ${docDate.padEnd(11)} ${doc.totalWithTax.toFixed(2).padStart(8)}\n`;
      acc.push(...encoder.encode(docLine));

      // Customer name if present
      if (doc.customerName) {
        const customerLine = `  ${doc.customerName.substring(0, lineWidth - 2)}\n`;
        acc.push(...encoder.encode(customerLine));
      }

      // Payment details
      doc.payments.forEach((payment) => {
        const paymentLabel = replaceSumniki(getPaymentTypeLabel(payment.type));
        const paymentLine = `  ${paymentLabel}: ${payment.amount.toFixed(2).padStart(lineWidth - 1 - paymentLabel.length - 4)}\n`;
        acc.push(...encoder.encode(paymentLine));
      });

      // Tax details
      doc.taxes.forEach((tax) => {
        const taxLine = `  ${t`DDV`} ${tax.taxRate}%: ${tax.taxAmount.toFixed(2).padStart(lineWidth - 12)}\n`;
        acc.push(...encoder.encode(taxLine));
      });

      // Add separator line
      acc.push(...encoder.encode("-".repeat(lineWidth - 1) + "\n"));

      return acc;
    }, [] as number[]),

    // Footer total
    0x1b,
    0x45,
    0x01, // Bold on
    ...encoder.encode("=".repeat(lineWidth - 1) + "\n"),
    ...(() => {
      const line = createJustifiedLine(`${t`SKUPAJ`}:`, totalAmount);
      return encoder.encode(line + "\n");
    })(),
    0x1b,
    0x45,
    0x00, // Bold off
    ...encoder.encode("=".repeat(lineWidth - 1) + "\n"),

    0x0a, // New line

    // Center alignment for footer
    0x1b,
    0x61,
    0x01, // Center alignment
    ...encoder.encode("*".repeat(lineWidth - 1) + "\n"),
    ...encoder.encode(centerText(t`Hvala za obisk!`) + "\n"),
    ...encoder.encode(centerText(t`www.lime-booking.si`) + "\n"),

    // Reset alignment
    0x1b,
    0x61,
    0x00, // Left alignment

    0x0a, // New line
    0x0a, // New line
    0x0a, // New line
    0x0a, // New line
    0x0a, // New line

    ...paddingBottomCommands,
  ];
};
